/**
 * Client-side API error handler utility
 * Extracts exact error messages from API responses
 * Matches the format returned by apiProxy on the server
 */

export interface ApiErrorResponse {
  status?: string | number;
  message?: string;
  errors?: Record<string, string[]>;
  data?: unknown;
}

export interface HandledApiError extends Error {
  message: string;
  apiStatus?: string;
  httpStatus?: number;
  errors?: Record<string, string[]>;
}

/**
 * Extracts exact error message from API response
 * Falls back to HTTP status text if no message available
 * @param response - The fetch response object
 * @returns Promise<HandledApiError> - Properly formatted error
 */
export async function handleApiErrorResponse(
  response: Response,
): Promise<HandledApiError> {
  let data: unknown;
  const contentType = response.headers.get("content-type");

  try {
    if (contentType?.includes("application/json")) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text };
    }
  } catch {
    data = { message: response.statusText };
  }

  const errorData = data as ApiErrorResponse;

  // Extract the exact error message in order of preference
  const errorMessage =
    errorData?.message ||
    (typeof errorData?.data === "string" ? errorData.data : null) ||
    response.statusText ||
    `Request failed with status ${response.status}`;

  const error: HandledApiError = new Error(errorMessage) as HandledApiError;
  error.message = errorMessage;
  error.apiStatus = errorData?.status ? String(errorData.status) : undefined;
  error.httpStatus = response.status;
  error.errors = errorData?.errors;

  return error;
}

/**
 * Helper to extract user-friendly message from error
 * For display in UI (shows exact API message)
 */
export function getUserErrorMessage(error: unknown): string {
  if (!error) return "An error occurred";

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "object" && "message" in error) {
    return String((error as Record<string, unknown>).message);
  }

  return String(error);
}
